import { z } from 'zod';
import { dateString, noteText, optionalText, pagination, rupees, shortText } from './common.js';
/**
 * Petty-cash / operational spending: vegetables, cleaning supplies, delivery
 * charges, minor repairs. Deliberately not a general ledger - see
 * apps/api/src/modules/expenses/service.ts for what this module is and isn't.
 */
/** ROOM/ONLINE are guest-invoice settlement rails only - not how you pay a vendor. */
export const EXPENSE_PAYMENT_MODES = ['CASH', 'CARD', 'UPI', 'BANK_TRANSFER', 'OTHER'];
export const expensePaymentModeEnum = z.enum(EXPENSE_PAYMENT_MODES);
export const EXPENSE_STATUSES = ['POSTED', 'VOID'];
/**
 * Starter categories seeded onto every property - at registration
 * (registerHotel in modules/public/service.ts) and, for hotels that
 * registered before this list existed, via the backfill script
 * (scripts/backfill-expense-categories.ts). Not a closed enum: the owner can
 * rename, deactivate or add to these freely once seeded; this is only the
 * one-time starting point. Order here is also the seeded sortOrder.
 */
export const DEFAULT_EXPENSE_CATEGORIES = [
    '🍅 Food & Ingredients',
    '🥤 Beverages & Supplies',
    '📦 Packaging & Takeaway',
    '🧹 Cleaning & Sanitation',
    '🔧 Maintenance & Repairs',
    '🔥 Gas & Fuel',
    '🚚 Transportation & Delivery',
    '🧾 Office & Stationery',
    '👨‍🍳 Staff Welfare',
    '🍳 Kitchen Supplies',
    '🛠️ Equipment & Small Appliances',
    'Other / Miscellaneous',
];
/**
 * Which daily report a category's spending is charged to. SHARED is the
 * building's own spending (cleaning, repairs, stationery): only the overall
 * daily report deducts it, so the F&B and front desk reports never both
 * claim the same rupee.
 */
export const EXPENSE_DEPARTMENTS = ['FNB', 'FRONT_DESK', 'SHARED'];
export const expenseDepartmentEnum = z.enum(EXPENSE_DEPARTMENTS);
/** The starter categories that are plainly kitchen spending; the rest start SHARED. */
export const FNB_DEFAULT_EXPENSE_CATEGORIES = [
    '🍅 Food & Ingredients',
    '🥤 Beverages & Supplies',
    '📦 Packaging & Takeaway',
    '🔥 Gas & Fuel',
    '🍳 Kitchen Supplies',
];
export const createExpenseCategorySchema = z.object({
    name: shortText,
    description: noteText,
    sortOrder: z.number().int().min(0).max(9999).default(0),
    /*
     * Optional with no schema default: update reuses this shape via partial(),
     * and a default here would quietly reset the department on every edit that
     * did not mention it. createCategory() supplies SHARED instead.
     */
    department: expenseDepartmentEnum.optional(),
});
/* sortOrder re-declared without its default: Zod 4 applies .default() inside
   .partial(), so an edit that did not send it reset the order to 0. */
export const updateExpenseCategorySchema = createExpenseCategorySchema
    .partial()
    .extend({ sortOrder: z.number().int().min(0).max(9999).optional() });
export const createExpenseSchema = z.object({
    categoryId: z.string().uuid(),
    amount: rupees.refine((v) => v > 0, 'Enter an amount greater than zero'),
    paymentMode: expensePaymentModeEnum,
    expenseDate: dateString,
    vendorName: optionalText,
    referenceNumber: optionalText,
    description: shortText,
    notes: noteText,
});
export const voidExpenseSchema = z.object({
    reason: shortText,
});
export const expenseFiltersSchema = z.object({
    from: dateString.optional(),
    to: dateString.optional(),
    categoryId: z.string().uuid().optional(),
    createdById: z.string().uuid().optional(),
    paymentMode: expensePaymentModeEnum.optional(),
    status: z.enum(EXPENSE_STATUSES).optional(),
    q: z.string().trim().max(80).optional(),
    ...pagination.shape,
});
export const expenseReportRangeSchema = z.object({
    from: dateString,
    to: dateString,
});
export const expenseDailySummarySchema = z.object({
    date: dateString.optional(),
});
/**
 * How much cash was physically put in the till this morning, before the
 * first sale - see CashDrawer in schema.prisma. Always today: there is no
 * legitimate reason to declare an opening float for a day that has already
 * happened, and allowing it would let someone retroactively rewrite a past
 * day's cash reconciliation.
 */
export const setOpeningCashSchema = z.object({
    amount: rupees.refine((v) => v >= 0, 'Enter an amount of zero or more'),
});
//# sourceMappingURL=expenses.js.map